// src/pages/Admin/SuperAdminDashboard.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GpxUpload from './GpxUpload';
import WaypointsBuilder from './WaypointsBuilder';
import {
  getProductById,
  getWaypointsById,
} from '../../globalService/api.service';
import { WayPoint } from '../../globalService/api.model';

const PREVIEW_USER_ID = 'PREVIEW';
const generateOrderId = () => Date.now().toString();

interface SuperAdminProd {
  is_active: boolean;
  id: string;
  name: string;
  images: string;
  url: string;
  totalDistanceKm: string;
  price: string;
  timeframeInDays: number;
  linkedProductId: string;
  saved: boolean;
}

const SuperAdminDashboard: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_HOST!;

  // Product form state
  const [prodData, setProdData] = useState<SuperAdminProd>({
    is_active: true,
    id: '',
    name: '',
    images: '',
    url: '',
    totalDistanceKm: '',
    price: '',
    timeframeInDays: 0,
    linkedProductId: '',
    saved: false,
  });
  const [prodMsg, setProdMsg] = useState<string>('');
  const [loadId, setLoadId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [previewReload, setPreviewReload] = useState<number>(0);

  // Existing waypoints
  const [initialWps, setInitialWps] = useState<WayPoint[]>([]);

  // Exit
  const handleExit = () => {
    auth.logout();
    navigate('/login', { replace: true });
  };

  // Load product (real or stub)
  const loadProduct = async () => {
    if (!loadId) return;
    setLoading(true);
    setProdMsg('');
    try {
      const prod = await getProductById(loadId);
      if (!prod) throw new Error('Not found');

      setProdData({
        is_active: true,
        id: prod.id.toString(),
        name: prod.name || '',
        images: prod.images || '',
        url: prod.url || '',
        totalDistanceKm: '',
        price: prod.price || '',
        timeframeInDays: 0,
        linkedProductId: '',
        saved: true,
      });
      setProdMsg('Loaded product from server');
    } catch {
      setProdData(d => ({
        ...d,
        id: loadId,
        saved: true,
      }));
      setProdMsg('Loaded stub for product ID ' + loadId);
    } finally {
      setLoading(false);
      setPreviewReload(r => r + 1);
    }
  };

  // Save product & enroll preview user
  const saveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setProdMsg('');
    try {
      const payload = {
        is_active: prodData.is_active,
        id: prodData.id,
        name: prodData.name,
        images: prodData.images,
        url: prodData.url,
        totalDistanceKm: prodData.totalDistanceKm,
        price: prodData.price,
        timeframeInDays: prodData.timeframeInDays,
        linkedProductId: prodData.linkedProductId,
      };

      const res1 = await fetch(`${API}/medalmad/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res1.ok) throw new Error(await res1.text());

      await fetch(`${API}/medalmad/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: PREVIEW_USER_ID,
          productIds: [payload.id],
          orderId: generateOrderId(),
        }),
      }).catch(() => {});

      setProdMsg('Product saved & preview user enrolled');
      setProdData(d => ({ ...d, saved: true }));
      setPreviewReload(r => r + 1);
    } catch (err: any) {
      setProdMsg(`Error: ${err.message}`);
    }
  };

  // Fetch existing waypoints on load/save and on every previewReload
  useEffect(() => {
    if (prodData.saved && prodData.id) {
      getWaypointsById(prodData.id, PREVIEW_USER_ID)
        .then(wps => setInitialWps(wps || []))
        .catch(() => setInitialWps([]));
    }
  }, [prodData.saved, prodData.id, previewReload]);

  // Callbacks to bump reload
  const onGpxSuccess = () => setPreviewReload(r => r + 1);
  const onWpSuccess  = () => setPreviewReload(r => r + 1);

  // Handle form inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setProdData(d => ({
      ...d,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>SuperAdmin Dashboard</h1>
        <button className="btn btn-outline-danger" onClick={handleExit}>
          Exit
        </button>
      </div>

      {/* Load Existing Product */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-4 mx-auto">
          <label className="form-label">Load Existing Product by ID</label>
          <div className="input-group">
            <input
              className="form-control"
              placeholder="Product ID"
              value={loadId}
              onChange={e => setLoadId(e.target.value.trim())}
            />
            <button
              className="btn btn-outline-primary"
              onClick={loadProduct}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load'}
            </button>
          </div>
        </div>
      </div>
      {prodMsg && <div className="alert alert-info">{prodMsg}</div>}

      <div className="row g-4">
        {/* Product Form */}
        <div className="col-md-4">
          <div className="card border-primary h-100">
            <div className="card-header bg-primary text-white">
              Create / Edit Product
            </div>
            <div className="card-body">
              <form onSubmit={saveProduct}>
                {/* Active */}
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    name="is_active"
                    className="form-check-input"
                    checked={prodData.is_active}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                  <label className="form-check-label">Active</label>
                </div>
                {/* ID */}
                <div className="mb-3">
                  <label className="form-label">Product ID</label>
                  <input
                    name="id"
                    className="form-control"
                    value={prodData.id}
                    onChange={handleChange}
                    required
                    disabled={prodData.saved}
                  />
                </div>
                {/* Linked ID */}
                <div className="mb-3">
                  <label className="form-label">Linked Product ID</label>
                  <input
                    name="linkedProductId"
                    className="form-control"
                    value={prodData.linkedProductId}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                </div>
                {/* Name */}
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    name="name"
                    className="form-control"
                    value={prodData.name}
                    onChange={handleChange}
                    required
                    disabled={prodData.saved}
                  />
                </div>
                {/* Image */}
                <div className="mb-3">
                  <label className="form-label">
                    Add Challenge Catalogue Image
                  </label>
                  <input
                    name="images"
                    className="form-control"
                    value={prodData.images}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                </div>
                {/* Website */}
                <div className="mb-3">
                  <label className="form-label">Add a Website</label>
                  <input
                    name="url"
                    type="url"
                    className="form-control"
                    placeholder="https://"
                    value={prodData.url}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                </div>
                {/* Distance */}
                <div className="mb-3">
                  <label className="form-label">
                    Enter Your Challenge Distance (km)
                  </label>
                  <input
                    name="totalDistanceKm"
                    type="number"
                    className="form-control"
                    value={prodData.totalDistanceKm}
                    onChange={handleChange}
                    required
                    disabled={prodData.saved}
                  />
                </div>
                {/* Price */}
                <div className="mb-3">
                  <label className="form-label">Price</label>
                  <input
                    name="price"
                    className="form-control"
                    value={prodData.price}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                </div>
                {/* Timeframe */}
                <div className="mb-3">
                  <label className="form-label">Timeframe (days)</label>
                  <input
                    name="timeframeInDays"
                    type="number"
                    className="form-control"
                    value={prodData.timeframeInDays}
                    onChange={handleChange}
                    disabled={prodData.saved}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-success w-100"
                  disabled={prodData.saved}
                >
                  Save & Enrol Preview
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* GPX Upload */}
        <div className="col-md-4">
          <div className="card border-info h-100">
            <div className="card-header bg-info text-white">
              Upload GPX File
            </div>
            <div className="card-body">
              <GpxUpload
                productId={prodData.id}
                disabled={!prodData.saved}
                onUploadSuccess={onGpxSuccess}
              />
            </div>
          </div>
        </div>

        {/* Waypoints */}
        <div className="col-md-4">
          <div className="card border-warning h-100">
            <div className="card-header bg-warning text-dark">
              Build Waypoints
            </div>
            <div className="card-body">
              <WaypointsBuilder
                productId={prodData.id}
                disabled={!prodData.saved}
                initialWaypoints={initialWps}
                onSaveSuccess={onWpSuccess}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Map Preview */}
      {prodData.saved && (
        <div className="mt-5">
          <h2 className="mb-3">Map Preview</h2>
          <div className="ratio ratio-16x9">
            <iframe
              key={previewReload}
              src={`https://platform.virtual-challenge.com/map/medalmad/${prodData.id}/${PREVIEW_USER_ID}?r=${previewReload}`}
              title="Route Preview"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
